import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import Navbar from './Navbar';
import Footer from './Footer';
import GuestChatWidget from './GuestChatWidget';
import './landing.scss';
import SeoHead from '../../../Components/Common/SeoHead';
import HelpBreadcrumbSchema from '../../../Components/Common/HelpBreadcrumbSchema';
import HelpScreenshot from './HelpScreenshot';
import type { HelpImage } from './HelpScreenshot';

const HelpArticleGettingStarted: React.FC = () => {
  const BASE = '/images/help/getting-started';

  const screenshots: Record<string, HelpImage[]> = {
    step1: [
      { src: `${BASE}/gs-step-1.png`, alt: 'Coderom sign up page', caption: 'Step 1 – Create your account at app.coderom.com' },
    ],
    step2: [
      { src: `${BASE}/gs-step-2.png`,   alt: 'eBay connection settings page',              caption: 'Step 2 – Go to Settings → eBay Connection' },
      { src: `${BASE}/gs-step-2-1.png`, alt: 'Connect eBay account button',                caption: 'Step 2.1 – Click Connect eBay Account' },
      { src: `${BASE}/gs-step-2-2.png`, alt: 'eBay OAuth consent screen',                  caption: 'Step 2.2 – Sign in to your eBay seller account' },
      { src: `${BASE}/gs-step-2-3.png`, alt: 'eBay permissions screen',                    caption: 'Step 2.3 – Grant Coderom permissions' },
      { src: `${BASE}/gs-step-2-4.png`, alt: 'eBay connection confirmed',                  caption: 'Step 2.4 – Connection confirmed' },
      { src: `${BASE}/gs-step-2-5.png`, alt: 'eBay store connected dashboard', caption: 'Step 2.5 – Your eBay store is now connected' },
    ],
    step3: [
      { src: `${BASE}/gs-step-3.png`,   alt: 'Add product search interface',   caption: 'Step 3 – Search for an Amazon product to import' },
      { src: `${BASE}/gs-step-3-1.png`, alt: 'Product draft preview',          caption: 'Step 3.1 – Review and edit your product draft' },
    ],
    step4: [
      { src: `${BASE}/gs-step-4.png`,   alt: 'Coderom settings page',          caption: 'Step 4 – Configure your pricing and stock settings' },
      { src: `${BASE}/gs-step-4-1.png`, alt: 'Repricing rules configuration',  caption: 'Step 4.1 – Set up repricing rules' },
    ],
    step5: [
      { src: `${BASE}/gs-step-5.png`, alt: 'Coderom dashboard with live listings', caption: 'Step 5 – Your listings are now live on eBay' },
    ],
  };

  return (
    <div className="layout-wrapper landing">
      <SeoHead
        title="Getting Started with CodeRom | CodeRom Help"
        description="Learn how to set up your CodeRom account, connect eBay & start dropshipping in minutes."
      />
      <HelpBreadcrumbSchema slug="getting-started" name="Getting Started" />
      <Navbar />

      <div className="help-article-page">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} xl={8}>
              {/* Breadcrumb */}
              <div className="breadcrumb-section">
                <Link to="/help" className="breadcrumb-link">Help Center</Link>
                <span className="breadcrumb-separator">›</span>
                <span className="breadcrumb-current">Getting Started</span>
              </div>

              {/* Article Header */}
              <div className="article-header">
                <h1>Getting Started with Coderom</h1>
                <p className="article-subtitle">
                  Everything you need to start selling on eBay with Amazon as your supplier.
                  We'll walk you through setup, your first product listing, and key settings.
                </p>
                <div className="article-meta">
                  <span className="read-time">📖 10 min read</span>
                  <span className="category">Getting Started</span>
                </div>
              </div>

              {/* Table of Contents */}
              <div className="table-of-contents">
                <h3>📋 On this page</h3>
                <ul>
                  <li><a href="#step1">1. Create your account</a></li>
                  <li><a href="#step2">2. Connect your eBay store</a></li>
                  <li><a href="#step3">3. Add your first product</a></li>
                  <li><a href="#step4">4. Configure essential settings</a></li>
                  <li><a href="#step5">5. Publish to eBay</a></li>
                </ul>
              </div>

              {/* Article Content */}
              <div className="article-content">

                {/* Step 1 */}
                <div id="step1" className="content-section">
                  <h2>Step 1: Create your account</h2>
                  <p>
                    Go to <strong>app.coderom.com</strong> and click <strong>Sign Up</strong>.
                    Enter your email address and create a password. You'll receive a confirmation
                    email — click the link to activate your account.
                  </p>

                  <HelpScreenshot images={screenshots.step1} stepLabel="Step 1: Create your account" />
                </div>

                {/* Step 2 */}
                <div id="step2" className="content-section">
                  <h2>Step 2: Connect your eBay store</h2>
                  <p>
                    After logging in, go to <strong>Settings → eBay Connection</strong>.
                    Click <strong>Connect eBay Account</strong> and sign in to your eBay seller account.
                    Coderom will request permission to manage your listings — click <strong>Agree</strong> to
                    complete the connection.
                  </p>

                  <HelpScreenshot images={screenshots.step2} stepLabel="Step 2: Connect your eBay store" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> You can connect multiple eBay stores (GB, US, DE, CA, AU, ES)
                      from the same settings page. Each store is managed separately.
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div id="step3" className="content-section">
                  <h2>Step 3: Add your first product</h2>
                  <p>There are two ways to add products:</p>

                  <h3>Option A: Single product</h3>
                  <p>
                    Go to <strong>Add Product</strong>, paste an Amazon ASIN or URL, and click <strong>Search</strong>.
                    Coderom will fetch the product details automatically.
                  </p>

                  <h3>Option B: Bulk import</h3>
                  <p>
                    Go to <strong>Bulk Import</strong>, upload a CSV file with Amazon ASINs (one per row),
                    and click <strong>Import</strong>. All products will be queued for processing.
                  </p>

                  <HelpScreenshot images={screenshots.step3} stepLabel="Step 3: Add your first product" />
                </div>

                {/* Step 4 */}
                <div id="step4" className="content-section">
                  <h2>Step 4: Configure essential settings</h2>
                  <p>
                    Before publishing, review these key settings in <strong>Settings → Pricing</strong>:
                  </p>

                  <div className="settings-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Setting</th>
                          <th>What it does</th>
                          <th>Recommended</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Profit margin</strong></td>
                          <td>Your markup percentage on top of the Amazon price</td>
                          <td>20–30%</td>
                        </tr>
                        <tr>
                          <td><strong>eBay fees</strong></td>
                          <td>Automatically deducted from your selling price calculation</td>
                          <td>12.8% (UK default)</td>
                        </tr>
                        <tr>
                          <td><strong>Smart repricing</strong></td>
                          <td>Automatically adjusts your eBay price when the Amazon price changes</td>
                          <td>On</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <HelpScreenshot images={screenshots.step4} stepLabel="Step 4: Configure essential settings" />
                </div>

                {/* Step 5 */}
                <div id="step5" className="content-section">
                  <h2>Step 5: Publish to eBay</h2>
                  <p>
                    Once your product is ready, go to <strong>Draft Products</strong>, select the items
                    you want to list, and click <strong>Publish to eBay</strong>. Coderom will create the
                    eBay listing with optimised titles, descriptions, and images.
                  </p>
                  <p>
                    After publishing, your product moves to <strong>Active Products</strong> where Coderom
                    monitors the Amazon price and stock in real-time, automatically updating your eBay listing.
                  </p>

                  <HelpScreenshot images={screenshots.step5} stepLabel="Step 5: Publish to eBay" />

                  <div className="success-box">
                    <div className="success-icon">🎉</div>
                    <div className="success-content">
                      <strong>Congratulations!</strong> You've successfully set up your Coderom account and
                      published your first product to eBay. Your listing is now live and being monitored 24/7.
                    </div>
                  </div>
                </div>

                {/* What's Next */}
                <div className="whats-next-section">
                  <h2>What's next?</h2>
                  <p>Now that you're set up, explore these features to maximise your profits:</p>
                  <div className="next-steps">
                    <Link to="/help/ai-listing" className="next-step-card">
                      <div className="next-step-icon">🤖</div>
                      <div className="next-step-content">
                        <h4>AI-powered product listing</h4>
                        <p>Let GPT-4o create optimised titles and descriptions automatically</p>
                      </div>
                    </Link>
                    <Link to="/help/pricing" className="next-step-card">
                      <div className="next-step-icon">💰</div>
                      <div className="next-step-content">
                        <h4>Smart repricing & pricing rules</h4>
                        <p>Automatically adjust prices based on Amazon changes and market conditions</p>
                      </div>
                    </Link>
                    <Link to="/help/amazon-filters" className="next-step-card">
                      <div className="next-step-icon">🔍</div>
                      <div className="next-step-content">
                        <h4>Amazon supplier filters</h4>
                        <p>Set quality rules to automatically select the best Amazon offers</p>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Was This Helpful */}
                <div className="helpful-section">
                  <h3>Was this helpful?</h3>
                  <div className="helpful-buttons">
                    <button className="helpful-btn helpful-yes">👍 Yes</button>
                    <button className="helpful-btn helpful-no">👎 No</button>
                  </div>
                  <p className="helpful-text">
                    Still need help? <Link to="/help">Browse all articles</Link> or{' '}
                    <a href="mailto:support@coderom.com">contact support</a>.
                  </p>
                </div>

              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <style>{`
        .help-article-page {
          padding: 120px 0 80px;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
          min-height: 100vh;
        }

        /* Breadcrumb */
        .breadcrumb-section {
          margin-bottom: 24px;
          font-size: 14px;
        }
        .breadcrumb-link {
          color: #6366f1;
          text-decoration: none;
          transition: color 0.2s;
        }
        .breadcrumb-link:hover {
          color: #4f46e5;
        }
        .breadcrumb-separator {
          margin: 0 8px;
          color: #cbd5e1;
        }
        .breadcrumb-current {
          color: #64748b;
        }

        /* Article Header */
        .article-header {
          margin-bottom: 40px;
        }
        .article-header h1 {
          font-size: 42px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          line-height: 1.2;
        }
        .article-subtitle {
          font-size: 18px;
          color: #64748b;
          line-height: 1.6;
          margin-bottom: 20px;
        }
        .article-meta {
          display: flex;
          gap: 16px;
          align-items: center;
          font-size: 14px;
        }
        .read-time {
          color: #64748b;
        }
        .category {
          background: #f1f5f9;
          color: #6366f1;
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 500;
        }

        /* Table of Contents */
        .table-of-contents {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 40px;
        }
        .table-of-contents h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .table-of-contents ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .table-of-contents li {
          margin-bottom: 8px;
        }
        .table-of-contents a {
          color: #6366f1;
          text-decoration: none;
          font-size: 15px;
          transition: color 0.2s;
        }
        .table-of-contents a:hover {
          color: #4f46e5;
          text-decoration: underline;
        }

        /* Article Content */
        .article-content {
          color: #334155;
          font-size: 16px;
          line-height: 1.8;
        }
        .content-section {
          margin-bottom: 48px;
          scroll-margin-top: 100px;
        }
        .content-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          margin-top: 40px;
        }
        .content-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #334155;
          margin-top: 24px;
          margin-bottom: 12px;
        }
        .content-section p {
          margin-bottom: 16px;
        }
        .content-section strong {
          color: #1e293b;
          font-weight: 600;
        }

        /* Tip Box */
        .tip-box {
          display: flex;
          gap: 12px;
          background: #eff6ff;
          border-left: 4px solid #3b82f6;
          padding: 16px;
          border-radius: 8px;
          margin: 24px 0;
        }
        .tip-icon {
          font-size: 20px;
          flex-shrink: 0;
        }
        .tip-content {
          font-size: 15px;
          line-height: 1.6;
          color: #1e40af;
        }

        /* Success Box */
        .success-box {
          display: flex;
          gap: 12px;
          background: #f0fdf4;
          border-left: 4px solid #10b981;
          padding: 16px;
          border-radius: 8px;
          margin: 24px 0;
        }
        .success-icon {
          font-size: 20px;
          flex-shrink: 0;
        }
        .success-content {
          font-size: 15px;
          line-height: 1.6;
          color: #065f46;
        }

        /* Settings Table */
        .settings-table {
          margin: 24px 0;
          overflow-x: auto;
        }
        .settings-table table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .settings-table th {
          background: #f8fafc;
          color: #1e293b;
          font-weight: 600;
          text-align: left;
          padding: 12px 16px;
          border-bottom: 2px solid #e2e8f0;
          font-size: 14px;
        }
        .settings-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f1f5f9;
          font-size: 15px;
        }
        .settings-table tbody tr:last-child td {
          border-bottom: none;
        }
        .settings-table tbody tr:hover {
          background: #f8fafc;
        }

        /* What's Next Section */
        .whats-next-section {
          margin-top: 60px;
          padding-top: 40px;
          border-top: 2px solid #e2e8f0;
        }
        .whats-next-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .next-steps {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 24px;
        }
        .next-step-card {
          display: flex;
          gap: 16px;
          padding: 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.3s;
        }
        .next-step-card:hover {
          border-color: #6366f1;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
          transform: translateY(-2px);
        }
        .next-step-icon {
          font-size: 32px;
          flex-shrink: 0;
        }
        .next-step-content h4 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }
        .next-step-content p {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }

        /* Was This Helpful */
        .helpful-section {
          margin-top: 60px;
          padding: 32px;
          background: #f8fafc;
          border-radius: 12px;
          text-align: center;
        }
        .helpful-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 20px;
        }
        .helpful-buttons {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-bottom: 16px;
        }
        .helpful-btn {
          padding: 10px 24px;
          border: 2px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .helpful-btn:hover {
          border-color: #6366f1;
          background: #eff6ff;
        }
        .helpful-text {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }
        .helpful-text a {
          color: #6366f1;
          text-decoration: none;
        }
        .helpful-text a:hover {
          text-decoration: underline;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .help-article-page {
            padding: 100px 0 60px;
          }
          .article-header h1 {
            font-size: 32px;
          }
          .article-subtitle {
            font-size: 16px;
          }
          .content-section h2 {
            font-size: 24px;
          }
          .settings-table {
            font-size: 14px;
          }
          .next-step-card {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>

      <Footer />
      <GuestChatWidget />
    </div>
  );
};

export default HelpArticleGettingStarted;
